import api from './axiosInterceptor';

/**
 * Branch Data Service
 * API client for Branch agentlist operations
 * Mirrors dataService.js but targets Branch tables
 */
export const branchDataService = {
  // ============================================================
  // GET ENDPOINTS - Data retrieval
  // ============================================================

  getCountries: (agent = null) => {
    const params = agent ? { agent } : {};
    return api.get('/api/branch-data/countries', { params });
  },

  getAgents: (country = null) => {
    const params = country ? { country } : {};
    return api.get('/api/branch-data/agents', { params });
  },

  getUploaders: (country = null, agent = null) => {
    const params = {};
    if (country) params.country = country;
    if (agent) params.agent = agent;
    return api.get('/api/branch-data/uploaders', { params });
  },

  searchByCountry: (country) => {
    return api.get(`/api/branch-data/search/country/${encodeURIComponent(country)}`);
  },

  searchByAgent: (agent) => {
    return api.get(`/api/branch-data/search/agent/${encodeURIComponent(agent)}`);
  },

  searchByUploader: (uploader) => {
    return api.get(`/api/branch-data/search/uploader/${encodeURIComponent(uploader)}`);
  },

  searchCombined: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.agent) params.append('agent', filters.agent);
    if (filters.uploader) params.append('uploader', filters.uploader);
    if (filters.status) params.append('status', filters.status);
    return api.get(`/api/branch-data/search/combined?${params.toString()}`);
  },

  searchAll: (query) => {
    return api.get(`/api/branch-data/search?q=${encodeURIComponent(query)}`);
  },

  getAllData: () => {
    return api.get('/api/branch-data/all');
  },
  getExportAllData: () => {
    return api.get('/api/branch-data/export-all');
  },

  // ============================================================
  // WRITE ENDPOINTS - Require Branch access (Admin, Editor Branch)
  // ============================================================

  updateAgent: (id, data) => {
    return api.put(`/api/branch-data/update/${id}`, data);
  },

  deleteAgent: (id) => {
    return api.delete(`/api/branch-data/delete/${id}`);
  },

  toggleFavorite: (id, isFavorite) => {
    return api.put(`/api/branch-data/favorite/${id}`, { is_favorite: isFavorite });
  },

  // ============================================================
  // PENDING QUEUE - Branch agents with is_pending=true
  // ============================================================

  getPendingAgents: () => {
    return api.get('/api/branch-data/queue/pending');
  },

  sendToQueue: (id, data) => {
    return api.put(`/api/branch-data/queue/send/${id}`, data);
  },

  restoreFromQueue: (id) => {
    return api.put(`/api/branch-data/queue/restore/${id}`);
  },

  // ============================================================
  // AGENT CREATION - Create new Branch agent
  // ============================================================

  createAgent: (agentData) => {
    return api.post('/api/agents/branch', agentData);
  },

  // ============================================================
  // FILE UPLOAD - Upload Excel/CSV for Branch
  // ============================================================

  uploadFile: (formData) => {
    return api.post('/api/branch-upload/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  getUploadTemplate: () => {
    return api.get('/api/branch-upload/template');
  },

  // ============================================================
  // APPROVAL - Pending uploads approval
  // ============================================================

  getPendingUploads: () => {
    return api.get('/api/branch-approval/pending');
  },

  getPendingUploadsGrouped: () => {
    return api.get('/api/branch-approval/pending/grouped');
  },

  getPendingUploadsBatch: (fileHash) => {
    return api.get(`/api/branch-approval/pending/batch/${encodeURIComponent(fileHash)}`);
  },

  approveBatch: (fileHash) => {
    return api.post(`/api/branch-approval/approve/batch/${encodeURIComponent(fileHash)}`);
  },

  rejectBatch: (fileHash, reason = '') => {
    return api.post(`/api/branch-approval/reject/batch/${encodeURIComponent(fileHash)}`, { reason });
  },

  approveRecord: (id) => {
    return api.post(`/api/branch-approval/approve/${id}`);
  },

  rejectRecord: (id, reason = '') => {
    return api.post(`/api/branch-approval/reject/${id}`, { reason });
  },

  // ============================================================
  // BLACKLIST - Branch blacklist management
  // ============================================================

  getBlacklistAgents: (page = 1, limit = 50, search = '') => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    return api.get(`/api/branch-blacklist?${params.toString()}`);
  },

  getBlacklistStats: () => {
    return api.get('/api/branch-blacklist/stats');
  },

  addToBlacklist: (agentData) => {
    return api.post('/api/branch-blacklist', agentData);
  },

  moveToBlacklist: (id, sourceTable, blacklistReason) => {
    return api.put(`/api/branch-blacklist/move/${id}`, {
      source_table: sourceTable,
      blacklist_reason: blacklistReason
    });
  },

  restoreFromBlacklist: (id) => {
    return api.put(`/api/branch-blacklist/restore/${id}`);
  },

  updateBlacklistAgent: (id, agentData) => {
    return api.put(`/api/branch-blacklist/${id}`, agentData);
  },

  deleteFromBlacklist: (id) => {
    return api.delete(`/api/branch-blacklist/${id}`);
  }
};

export default branchDataService;
